// src/components/services/ServiceBookingCTA.tsx
//
// P2.12B-1: shared end-of-page booking next-action for service pages that
// previously ended without one. Deliberately generic and operational — it
// makes no claims about suitability, outcomes, duration, preparation or
// clinical process, so every service page can share it verbatim. Navigation
// is a real semantic anchor (Button asChild renders the Link's <a>), and no
// analytics events are attached — /book carries the booking funnel.

import Link from 'next/link'
import { Button } from '@/components/ui/button'

interface ServiceBookingCTAProps {
  /** Section heading; keep it operational, not clinical. */
  title?: string
  /** Show the secondary link back to the services hub (off on /services itself). */
  showServicesLink?: boolean
}

const ServiceBookingCTA: React.FC<ServiceBookingCTAProps> = ({
  title = 'Ready to take the next step?',
  showServicesLink = true,
}) => (
  <section
    aria-labelledby="service-booking-cta-heading"
    className="px-4 py-10 sm:py-12"
  >
    {/* Contained card on the site's neutral surface (same surface/border
        language as the services cards), not a full-bleed color band — the
        booking button carries the color emphasis. */}
    <div className="mx-auto max-w-6xl rounded-2xl sm:rounded-3xl border border-border bg-[#F3F3F3] px-6 py-8 sm:px-10 sm:py-9 flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-8">
      <h2
        id="service-booking-cta-heading"
        className="text-xl sm:text-2xl font-serif text-gray-900 text-center sm:text-left"
      >
        {title}
      </h2>
      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 shrink-0">
        <Button
          asChild
          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-3 rounded-full text-base transition-transform duration-300 hover:scale-105"
        >
          <Link href="/book">Book an Appointment</Link>
        </Button>
        {showServicesLink && (
          <Link
            href="/services"
            className="text-sm sm:text-base text-gray-600 hover:text-emerald-700 underline underline-offset-4"
          >
            View All Our Services
          </Link>
        )}
      </div>
    </div>
  </section>
)

export default ServiceBookingCTA
